/**
 * AnswerBlock — single dense passage (134-167 words) for AI citation.
 *
 * Per Princeton GEO research + Ahrefs Dec 2025: ChatGPT/Perplexity/AI Overviews
 * extract self-contained passages, not whole pages. Optimal length = 134-167 words.
 * Most AfficheServices content is fragmented into <50w paragraphs, which fails
 * the citation extractor. This block synthesizes a single quotable passage from
 * existing aiContent fields.
 *
 * Renders as <section> with question-phrased H2 + one paragraph. Schema-org
 * Question+Answer markup is added implicitly via parent FAQPage when relevant.
 */

interface AnswerBlockProps {
  question: string
  parts: Array<string | undefined | null>
  /** Min/max word count target (defaults: 134-167 per AI sweet spot) */
  minWords?: number
  maxWords?: number
}

function trimToWordRange(text: string, min: number, max: number): string {
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length <= max) return words.join(' ')
  const trimmed = words.slice(0, max).join(' ')
  const lastPeriod = trimmed.lastIndexOf('.')
  return lastPeriod > min * 5 ? trimmed.slice(0, lastPeriod + 1) : trimmed + '…'
}

export function AnswerBlock({ question, parts, minWords = 134, maxWords = 167 }: AnswerBlockProps) {
  const merged = parts
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .join(' ')

  const wordCount = merged.split(/\s+/).filter(Boolean).length
  if (wordCount < 60) return null

  const passage = trimToWordRange(merged, minWords, maxWords)

  return (
    <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-50/40 p-6">
      <h2 className="text-lg font-bold text-slate-900">{question}</h2>
      <p className="mt-3 text-sm leading-relaxed text-slate-700">{passage}</p>
    </section>
  )
}
